import {
  Checkbox,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
  InputLabel,
  FormHelperText,
  Box,
} from "@mui/material";
import { COVERAGE_STATES, STATES } from "@constants";
import { useGetPayorDataByName } from "hooks/api/payors/useGetPayorDataByName";
import {ANLoadingScreen } from "@answersnow/pkg-theme";
import PayorSelection from "./PayorSelection";
import React from "react";

export default function PlanDetailsTab({ onChange, data, providers, errors }) {
  const { payorData: enterPayorOptions, payorDataLoading: enterPayorDataLoading } =
    useGetPayorDataByName("enter");
  const { payorData: silnaPayorOptions, payorDataLoading: silnaPayorDataLoading } =
    useGetPayorDataByName("silna");
  const { payorData: candidPayorOptions, payorDataLoading: candidPayorDataLoading } =
    useGetPayorDataByName("candid");

  if (enterPayorDataLoading || silnaPayorDataLoading || candidPayorDataLoading)
    return <ANLoadingScreen />;

  return (
    <Stack gap={5}>
      <TextField
        label="Insurance company"
        onChange={onChange("insurance_id", null, data)}
        value={providers?.length === 1 ? providers[0].id : data.insurance_id ?? ""}
        select
        fullWidth
        disabled={providers?.length === 1}
      >
        <MenuItem disabled value="">
          Select select
        </MenuItem>
        {providers?.map(({ name, id, isMedicaid }, index) => (
          <MenuItem key={`${index}`} name={name} value={id} data-medic={isMedicaid}>
            {name}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        id="name"
        label="Name of plan"
        onChange={onChange("name", null, data)}
        value={data.name ?? ""}
        fullWidth
        inputProps={{ maxLength: 1000 }}
      />
      <TextField
        label="Coverage state"
        onChange={onChange("coverage_state", null, data)}
        value={data.coverage_state ?? ""}
        select
        fullWidth
      >
        <MenuItem disabled value="">
          Select select
        </MenuItem>
        {Object.keys(COVERAGE_STATES).map((key) => {
          return (
            <MenuItem key={key} value={key}>
              {COVERAGE_STATES[key as keyof typeof COVERAGE_STATES]}
            </MenuItem>
          );
        })}
      </TextField>
      <FormControlLabel
        control={
          <Checkbox checked={data.is_medicaid} onChange={onChange("is_medicaid", null, data)} />
        }
        label="Medicaid plan"
      />
      <Box display="flex" flexDirection="column" gap={3}>
        <InputLabel shrink={false}>Credentialing</InputLabel>
        <FormControlLabel
          control={
            <Checkbox
              checked={data.allows_backdating}
              onChange={(e) => {
                // If this box is UNchecked, also uncheck the monthly check-in box
                const checked = e.target.checked;
                onChange("allows_backdating", null, data)(e);
                if (!checked && data.requires_monthly_checkin) {
                  onChange("requires_monthly_checkin", null, {
                    ...data,
                    requires_monthly_checkin: false,
                  })({
                    target: { checked: false },
                  });
                }
              }}
            />
          }
          label="Staffed with credentialing submission"
        />
        <FormHelperText>
          When enabled, a client can be matched based on credential submission vs approval.
        </FormHelperText>
        <FormControlLabel
          control={
            <Checkbox
              checked={data.requires_monthly_checkin}
              onChange={onChange("requires_monthly_checkin", null, data)}
            />
          }
          label="Plan requires 15 min monthly check-in"
          disabled={!data.allows_backdating}
        />
      </Box>

      <PayorSelection
        options={silnaPayorOptions}
        label="Silna payor"
        onChange={onChange("silna_payor_uuid", null, data)}
        value={data.silna_payor_uuid ?? ""}
      />
      <PayorSelection
        options={candidPayorOptions}
        label="Candid payor"
        onChange={onChange("candid_payer_id", null, data)}
        value={data.candid_payer_id ?? ""}
      />
      <PayorSelection
        options={enterPayorOptions}
        label="Enter payor"
        onChange={onChange("eh_payer_id", null, data)}
        value={data.eh_payer_id ?? ""}
      />
      <TextField
        label="Phone number"
        id="phone"
        onChange={onChange("phone", "phoneNumberchange", data)}
        value={data.phone ?? ""}
        variant="outlined"
        size="small"
        fullWidth
        inputProps={{ maxLength: 14 }}
      />
      <TextField
        id="address_1"
        name="address_1"
        autoComplete="off"
        value={data.address_1 ?? ""}
        onChange={onChange("address_1", null, data)}
        variant="outlined"
        label="Address"
        fullWidth
        inputProps={{ maxLength: 1000 }}
      />
      <TextField
        id="address_2"
        label="Address 2"
        autoComplete="off"
        onChange={onChange("address_2", null, data)}
        value={data.address_2 ?? ""}
        inputProps={{ maxLength: 1000 }}
        fullWidth
      />
      <TextField
        id="state"
        select
        label="State"
        onChange={onChange("state", null, data)}
        value={data.state ?? ""}
        fullWidth
        autoComplete="off"
      >
        <MenuItem disabled value={""}>
          Select a state
        </MenuItem>
        {Object.keys(STATES).map((key) => {
          return (
            <MenuItem key={key} value={key}>
              {STATES[key as keyof typeof STATES]}
            </MenuItem>
          );
        })}
      </TextField>
      <TextField
        id="city"
        label="City"
        onChange={onChange("city", null, data)}
        value={data.city ?? ""}
        fullWidth
        inputProps={{ maxLength: 255 }}
      />
      <TextField
        id="zip"
        label="ZIP code"
        onChange={onChange("zip", "zipCode", data)}
        value={data.zip ?? ""}
        fullWidth
        inputProps={{ maxLength: 5 }}
        helperText={
          errors["zip"]
            ? "Please enter ZIP Code"
            : errors["zip_format_error"]
              ? "Invalid ZIP Code"
              : ""
        }
      />
    </Stack>
  );
}
